import React, { useState, useEffect, useRef } from 'react';
import { 
  PlayIcon, 
  StopIcon, 
  SpeakerWaveIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useToast } from '../contexts/ToastContext';

interface PureWebAudioCalibrationProps {
  onCalibrationComplete?: (result: { finalGain: number; finalVolume: number }) => void;
}

const PureWebAudioCalibration: React.FC<PureWebAudioCalibrationProps> = ({ onCalibrationComplete }) => {
  const { showSuccess, showError } = useToast();
  
  // State
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [targetDbfs, setTargetDbfs] = useState(-20);
  const [currentGain, setCurrentGain] = useState(0.5);
  const [currentMeasurement, setCurrentMeasurement] = useState(0);
  const [statusMessage, setStatusMessage] = useState('Ready');
  const [calibrationResult, setCalibrationResult] = useState<any>(null);
  const [iteration, setIteration] = useState(0);
  const [binarySearchMode, setBinarySearchMode] = useState(true);
  
  // Audio context and nodes
  const audioContextRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const microphoneStreamRef = useRef<MediaStream | null>(null);
  
  // Refs
  const calibrationIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCalibrationRunningRef = useRef(false);
  const currentGainRef = useRef(0.5);
  const gainLowRef = useRef(0.01);
  const gainHighRef = useRef(1.0);

  // Initialize audio context
  useEffect(() => {
    if (typeof window !== 'undefined') {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      if (calibrationIntervalRef.current) clearInterval(calibrationIntervalRef.current);
      stopAudio();
    };
  }, []);

  const stopAudio = () => {
    if (oscillatorRef.current) {
      oscillatorRef.current.stop();
      oscillatorRef.current = null;
    }
    if (gainNodeRef.current) {
      gainNodeRef.current = null;
    }
    if (microphoneStreamRef.current) {
      microphoneStreamRef.current.getTracks().forEach(track => track.stop());
      microphoneStreamRef.current = null;
    }
    if (microphoneRef.current) {
      microphoneRef.current = null;
    }
  };

  const generateCalibrationTone = (frequency: number, gain: number) => {
    if (!audioContextRef.current) return;

    const audioContext = audioContextRef.current;
    
    // Stop any existing tone
    if (oscillatorRef.current) {
      oscillatorRef.current.stop();
    }

    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, audioContext.currentTime);
    gainNode.gain.setValueAtTime(gain, audioContext.currentTime);

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.start();
    
    oscillatorRef.current = oscillator;
    gainNodeRef.current = gainNode;
  };

  const measureAudioLevel = async (): Promise<number> => {
    if (!audioContextRef.current) return -100;

    try {
      // Get microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: { 
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        } 
      });
      
      microphoneStreamRef.current = stream;
      const microphone = audioContextRef.current.createMediaStreamSource(stream);
      microphoneRef.current = microphone;

      // Create analyser for measuring audio level
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      microphone.connect(analyser);
      analyserRef.current = analyser;

      // Play calibration tone
      generateCalibrationTone(1000, currentGainRef.current);

      // Wait for audio to stabilize
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Measure audio level
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteFrequencyData(dataArray);

      // Calculate RMS
      let sum = 0;
      for (let i = 0; i < bufferLength; i++) {
        sum += dataArray[i] * dataArray[i];
      }
      const rms = Math.sqrt(sum / bufferLength);
      
      // Convert to dBFS (rough approximation)
      const dbfs = rms > 0 ? 20 * Math.log10(rms / 255) : -100;

      // Cleanup
      stopAudio();

      return dbfs;
    } catch (error) {
      console.error('Failed to measure audio level:', error);
      stopAudio();
      return -100;
    }
  };

  const adjustGain = (measurement: number): boolean => {
    const tolerance = 0.5;

    if (Math.abs(measurement - targetDbfs) <= tolerance) {
      setStatusMessage("On Target");
      return true;
    }

    const prev = currentGainRef.current;

    if (measurement < targetDbfs) {
      // Too quiet — raise the lower bound and bisect toward the upper bound
      gainLowRef.current = prev;
    } else {
      // Too loud — lower the upper bound and bisect toward the lower bound
      gainHighRef.current = prev;
    }

    // Geometric mean = midpoint in dB space (halves the remaining bracket each step)
    const newGain = Math.sqrt(gainLowRef.current * gainHighRef.current);
    currentGainRef.current = newGain;
    setCurrentGain(newGain);
    setStatusMessage(
      measurement < targetDbfs
        ? `Too quiet (${measurement.toFixed(1)} dBFS) → increasing gain: ${prev.toFixed(3)} → ${newGain.toFixed(3)}`
        : `Too loud (${measurement.toFixed(1)} dBFS) → decreasing gain: ${prev.toFixed(3)} → ${newGain.toFixed(3)}`
    );

    return false;
  };

  const startCalibration = async () => {
    try {
      isCalibrationRunningRef.current = true;
      gainLowRef.current = 0.01;
      gainHighRef.current = 1.0;
      currentGainRef.current = 0.5; // geometric mean of [0.01, 1.0] ≈ 0.1, but 0.5 is a reasonable start
      setIsCalibrating(true);
      setCalibrationResult(null);
      setCurrentGain(0.5); // Start at 50% gain
      setBinarySearchMode(true);
      setIteration(0);
      setStatusMessage("Starting calibration...");
      
      // Start calibration loop
      const maxIterations = 20;
      let currentIteration = 0;
      
      const calibrationLoop = async () => {
        if (!isCalibrationRunningRef.current || currentIteration >= maxIterations) {
          isCalibrationRunningRef.current = false;
          setIsCalibrating(false);
          if (currentIteration >= maxIterations) {
            setStatusMessage("Calibration timeout");
            showError('Calibration Timeout', 'Calibration took too long to complete');
          }
          return;
        }

        currentIteration++;
        setIteration(currentIteration);
        setStatusMessage(`Calibrating... Iteration ${currentIteration}`);

        // Measure audio level
        const measurement = await measureAudioLevel();
        setCurrentMeasurement(measurement);

        if (measurement === -100) {
          setStatusMessage("Error: No audio detected");
          isCalibrationRunningRef.current = false;
          setIsCalibrating(false);
          showError('Audio Detection Failed', 'Could not detect audio. Check microphone permissions.');
          return;
        }

        // Check for clipping
        if (measurement > -1.0) {
          setStatusMessage("Clipping detected");
          currentGainRef.current = currentGainRef.current * 0.8;
          setCurrentGain(currentGainRef.current);
          setTimeout(calibrationLoop, 500);
          return;
        }

        // Adjust gain
        const isOnTarget = adjustGain(measurement);
        
        if (isOnTarget) {
          setStatusMessage("Calibration complete!");
          isCalibrationRunningRef.current = false;
          setIsCalibrating(false);
          const result = {
            finalGain: currentGainRef.current,
            finalVolume: currentGainRef.current * 100,
            finalMeasurement: measurement,
            targetDbfs: targetDbfs,
            iterations: currentIteration
          };
          setCalibrationResult(result);
          showSuccess('Calibration Complete', `Successfully calibrated to ${measurement.toFixed(1)} dBFS`);
          onCalibrationComplete?.(result);
          return;
        }

        // Continue calibration
        setTimeout(calibrationLoop, 500);
      };

      // Start the calibration loop
      calibrationLoop();
      
    } catch (error) {
      console.error('Calibration error:', error);
      showError('Calibration Error', 'Failed to start calibration. Please check your audio setup.');
      setIsCalibrating(false);
    }
  };

  const stopCalibration = () => {
    isCalibrationRunningRef.current = false;
    setIsCalibrating(false);
    stopAudio();
    setStatusMessage("Stopped");
    showSuccess('Calibration Stopped', 'Calibration process has been stopped');
  };

  const testAudioLevel = async () => {
    try {
      const level = await measureAudioLevel();
      showSuccess('Audio Test', `Measured level: ${level.toFixed(1)} dBFS`);
    } catch (error) {
      showError('Audio Test Failed', 'Could not measure audio level. Check microphone permissions.');
    }
  };

  const getStatusColor = (message: string) => {
    if (message.includes('On Target') || message.includes('Complete')) {
      return 'text-green-600';
    } else if (message.includes('Error') || message.includes('Failed')) {
      return 'text-red-600';
    } else if (message.includes('Paused') || message.includes('Noise')) {
      return 'text-yellow-600';
    } else if (message.includes('Clipping')) {
      return 'text-orange-600';
    }
    return 'text-blue-600';
  };

  const getStatusIcon = (message: string) => {
    if (message.includes('On Target') || message.includes('Complete')) {
      return <CheckCircleIcon className="w-5 h-5 text-green-600" />;
    } else if (message.includes('Error') || message.includes('Failed')) {
      return <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />;
    } else if (message.includes('Paused') || message.includes('Noise')) {
      return <ClockIcon className="w-5 h-5 text-yellow-600" />;
    } else if (message.includes('Clipping')) {
      return <ExclamationTriangleIcon className="w-5 h-5 text-orange-600" />;
    }
    return <SpeakerWaveIcon className="w-5 h-5 text-blue-600" />;
  };

  const formatDbfs = (dbfs: number) => {
    if (dbfs === -Infinity || isNaN(dbfs)) return '-∞ dBFS';
    return `${dbfs.toFixed(1)} dBFS`;
  };

  const formatVolume = (volume: number) => {
    if (volume === null || volume === undefined || isNaN(volume)) return 'N/A';
    return `${Math.round(volume)}%`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          🔊 Pure Browser Audio Calibration
        </h3>
        <p className="text-sm text-gray-600">
          Calibrate your browser's audio output using only browser APIs. 
          No backend audio processing required - works entirely in your browser.
        </p>
      </div>

      {/* Target Level Control */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Target Level: {formatDbfs(targetDbfs)}
        </label>
        <input
          type="range"
          min="-40"
          max="-6"
          step="0.5"
          value={targetDbfs}
          onChange={(e) => setTargetDbfs(Number(e.target.value))}
          disabled={isCalibrating}
          className="w-full slider disabled:opacity-50"
          style={{ height: '8px' }}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>-40 dBFS</span>
          <span>-6 dBFS</span>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Recommended: -20 dBFS for comfortable listening
        </p>
      </div>

      {/* Control Buttons */}
      <div className="flex justify-center space-x-4 mb-6">
        {!isCalibrating ? (
          <>
            <button
              onClick={startCalibration}
              className="flex items-center space-x-2 bg-auris-teal text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
            >
              <PlayIcon className="w-5 h-5" />
              <span>Start Calibration</span>
            </button>
            <button
              onClick={testAudioLevel}
              className="flex items-center space-x-2 bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
            >
              <SpeakerWaveIcon className="w-5 h-5" />
              <span>Test Audio</span>
            </button>
          </>
        ) : (
          <button
            onClick={stopCalibration}
            className="flex items-center space-x-2 bg-red-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors"
          >
            <StopIcon className="w-5 h-5" />
            <span>Stop Calibration</span>
          </button>
        )}
      </div>

      {/* Real-time Status Display */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Real-time Status</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Status:</span>
              <div className="flex items-center space-x-2">
                {getStatusIcon(statusMessage)}
                <span className={`text-sm font-medium ${getStatusColor(statusMessage)}`}>
                  {statusMessage}
                </span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Measured Level:</span>
              <span className="text-sm font-mono font-medium">
                {formatDbfs(currentMeasurement)}
              </span>
            </div>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Target Level:</span>
              <span className="text-sm font-mono font-medium">
                {formatDbfs(targetDbfs)}
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Browser Gain:</span>
              <span className="text-sm font-mono font-medium">
                {formatVolume(currentGain * 100)}
              </span>
            </div>
          </div>
        </div>

        {/* Additional Status Info */}
        {iteration > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Iteration:</span>
              <span className="text-sm font-mono font-medium">
                {iteration}
              </span>
            </div>
          </div>
        )}

        {binarySearchMode && (
          <div className="mt-2">
            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Browser Audio Mode
            </span>
          </div>
        )}
      </div>

      {/* Calibration Result */}
      {calibrationResult && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-green-800 mb-2">
            Calibration Complete!
          </h4>
          
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-green-700">Final Level:</span>
                <span className="ml-2 font-mono font-medium">
                  {formatDbfs(calibrationResult.finalMeasurement)}
                </span>
              </div>
              <div>
                <span className="text-green-700">Target Level:</span>
                <span className="ml-2 font-mono font-medium">
                  {formatDbfs(calibrationResult.targetDbfs)}
                </span>
              </div>
              <div>
                <span className="text-green-700">Iterations:</span>
                <span className="ml-2 font-medium">
                  {calibrationResult.iterations}
                </span>
              </div>
              <div>
                <span className="text-green-700">Final Gain:</span>
                <span className="ml-2 font-mono font-medium">
                  {formatVolume(calibrationResult.finalVolume)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-800 mb-2">Instructions</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Grant microphone permissions when prompted</li>
          <li>• Make sure your speakers/headphones are connected</li>
          <li>• Find a quiet environment for best results</li>
          <li>• The system will adjust browser audio gain automatically</li>
          <li>• Use "Test Audio" to verify your setup before calibration</li>
          <li>• This works entirely in your browser - no backend required!</li>
        </ul>
      </div>
    </div>
  );
};

export default PureWebAudioCalibration;
